import { useEffect } from 'react';
import { Tabs } from 'antd';
import {
  EpisodePerformanceCardEcho,
  NetworksPerformanceCardEcho,
  ShowsPerformanceCardEcho,
} from '../../PerformanceCard/PerformanceCard';
import { cardFullWidth } from '@/utils/styleUtils';
import { useContentPerformanceOverviewQuery } from '../../useContentPerformanceOverviewQuery';
import { useDataLevelTabs, type LevelTabChangeTrigger, type LevelView } from './useDataLevelTabs';
import { useFilters } from '@/services/revenueApi';
import useNetworks from '@/hooks/useNetworks';

interface DataLevelTabsProps {
  /**
   * Controlled active tab. When omitted, tab state is owned internally via
   * {@link useDataLevelTabs} (Decibel / uncontrolled usage).
   */
  activeView?: LevelView;
  /**
   * Controlled change handler. When omitted, uses the internal
   * {@link useDataLevelTabs} handler (including Mixpanel).
   */
  onViewChange?: (view: LevelView, trigger?: LevelTabChangeTrigger) => void;
  /** `true` after the first successful sync; use as an `enabled` guard on data queries. */
  isSynced?: boolean;
  /** `false` when no HLS-enabled show is selected; hides the video metric columns in each table. */
  showVideoMetrics?: boolean;
}

/**
 * Tab bar for switching between Networks, Shows, and Episodes views on the Content
 * Performance page.
 *
 * Optionally controlled: Echo lifts state so filters can switch tabs; Decibel omits
 * `activeView` / `onViewChange` and uses the internal hook.
 */
const DataLevelTabs = ({
  activeView,
  onViewChange,
  isSynced = true,
  showVideoMetrics = true,
}: DataLevelTabsProps) => {
  const { activeView: internalActiveView, handleViewChange } = useDataLevelTabs();
  const resolvedActiveView = activeView ?? internalActiveView;
  const resolvedOnViewChange = onViewChange ?? handleViewChange;

  const { networks, shows: selectedShows, allShows } = useFilters();
  const { data: authorizedNetworks, isPending: isNetworksPending } = useNetworks();

  const { data: overviewData } = useContentPerformanceOverviewQuery({
    enabled: isSynced,
    dataSource: 'v3',
  });
  const episodeCount = overviewData?.totalEpisodes;

  const networkLevelCount = networks?.filter((network) => network.accessLevel === 'network').length;
  const hasNetworkAccess = authorizedNetworks?.some((network) => network.accessLevel === 'network');

  useEffect(() => {
    if (isNetworksPending) return;
    // If the user does not have network-level access to any networks, switch to the Shows view.
    // The network tab is not rendered in this case.
    if (!hasNetworkAccess && activeView === 'networks') {
      handleViewChange('shows');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNetworkAccess, activeView, isNetworksPending]);

  return (
    <Tabs
      destroyOnHidden
      className={cardFullWidth}
      activeKey={resolvedActiveView}
      onChange={(key) => resolvedOnViewChange(key as LevelView)}
      items={[
        ...(hasNetworkAccess
          ? [
              {
                key: 'networks',
                label: `Networks ${networkLevelCount ? `(${networkLevelCount})` : ''}`,
                children: (
                  <NetworksPerformanceCardEcho
                    isSynced={isSynced}
                    showVideoMetrics={showVideoMetrics}
                  />
                ),
              },
            ]
          : []),
        {
          key: 'shows',
          label: `Shows ${selectedShows?.length ? `(${selectedShows.length})` : `(${allShows?.length ?? 0})`}`,
          children: (
            <ShowsPerformanceCardEcho isSynced={isSynced} showVideoMetrics={showVideoMetrics} />
          ),
        },
        {
          key: 'episodes',
          label: `Episodes (${episodeCount ?? '…'})`,
          children: (
            <EpisodePerformanceCardEcho
              isSynced={isSynced}
              showVideoMetrics={showVideoMetrics}
              title="Episode Performance"
            />
          ),
        },
      ]}
    />
  );
};

export default DataLevelTabs;
